// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React, { Fragment, type Node, useEffect, useRef, useState } from 'react'; // eslint-disable-line no-unused-vars
import { noop } from 'lodash';

import Icon from '../Icon';
import TruncatedText from '../TruncatedText';
import { Colors } from '@zapier/theme';
import {
  createKeyHandler,
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

import findBreakIndex from './findBreakIndex';

/** @jsx isolateComponent */

export type Props = {
  /** Aria label for input */
  ariaLabel?: string,
  /** Auto focuses input */
  autoFocus?: boolean,
  /** How long a line must be before it's broken into multiple lines */
  breakLineLength?: number,
  /** If true any part of the text can be clicked to edit if false only the icon can be clicked */
  canClickText?: boolean,
  /** True to start edit without user interaction */
  defaultIsEditing?: boolean,
  /** True to hide underline */
  hideUnderline?: boolean,
  /** SVG title for edit icon */
  iconTitle?: string,
  /** True to show link color on hover */
  isEasyClick?: boolean,
  /** Max length of text input */
  maxLength?: number,
  /** Value name (will be returned in onChange, onClose) */
  name?: string,
  /** Called when text is changed in input */
  onChange: (value: string, name?: string) => void,
  /** Called when container is clicked */
  onContainerClick: () => void,
  /** Called when user closes editor */
  onClose: (value: string, name?: string, didConfirm: boolean) => void,
  /** Called to validate input */
  onValidate: (value: string, name?: string) => boolean,
  /** Placeholder value for editor */
  placeholder?: string,
  /** Custom text render */
  renderText?: ({ text: string }) => Node,
  /** Text input size */
  size?: number,
  /** Editor value */
  value: string,
};

const Styles = labelStyles(__filename, {
  root: (props, isEditing, isValid) => [
    {
      alignItems: 'center',
      borderBottom: '1px solid transparent',
      display: 'flex',

      ':focus': {
        outline: `1px dotted ${Colors.heroine}`,
      },
    },

    props.canClickText &&
      !isEditing &&
      withModifier('canClickText', {
        borderBottomColor: Colors.adamantium,
        cursor: 'pointer',

        ':hover': {
          borderBottomColor: Colors.heroine,
          borderBottomStyle: 'dashed',
        },
      }),

    props.hideUnderline &&
      withModifier('hideUnderline', {
        borderBottomColor: 'transparent',

        ':hover': {
          borderBottomColor: 'transparent',
        },
      }),

    !isValid &&
      withModifier('invalid', {
        borderBottomColor: Colors.status.error.border,

        ':hover': {
          borderBottomColor: Colors.status.error.border,
        },
      }),

    isEditing &&
      withModifier('isEditing', {
        borderBottomColor: Colors.heroine,

        ':hover': {
          borderBottomColor: Colors.heroine,
        },
      }),
  ],
  content: (props, isValid, isPlaceholder) => [
    {
      backgroundColor: 'transparent',
      border: 'none',
      color: Colors.batmobile,
      fontWeight: 'inherit',
      lineHeight: 1.5,
      margin: 0,
      padding: '1px 0',
      width: '100%',

      '::placeholder': {
        opacity: 0.6,
      },
    },

    props.isEasyClick &&
      withModifier('isEasyClick', {
        cursor: 'pointer',

        ':hover, :focus': {
          color: Colors.heroine,
          textDecoration: 'none',
        },
      }),

    isPlaceholder &&
      withModifier('isPlaceholder', {
        opacity: 0.6,
      }),

    !isValid &&
      withModifier('invalid', {
        background: Colors.status.error.bg,
      }),
  ],
  contentWrap: {
    display: 'block',
    flex: 1,
    overflow: 'hidden',
  },
  multiline: {
    borderBottomColor: Colors.adamantium,
    lineHeight: '1.7em',
  },
});

const InlineEditor = (props: Props) => {
  const [isEditing, setIsEditing] = useState(props.defaultIsEditing);
  const [isValid, setIsValid] = useState(
    props.onValidate(props.value, props.name)
  );
  const [originalValue, setOriginalValue] = useState(props.value);
  const textInput = useRef();

  useEffect(() => {
    if (isEditing) {
      // $FlowFixMe - flow doesn't understand useRef
      textInput.current.focus();
      // $FlowFixMe - flow doesn't understand useRef
      textInput.current.select();
    }
  }, [isEditing]);

  const toggleIsEditing = e => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      if (e) {
        // stops the page from scrolling down if you hit the spacebar to enter
        e.preventDefault();
      }
      setOriginalValue(props.value);
      props.onContainerClick();
    }
  };

  const handleChange = e => {
    setIsValid(props.onValidate(e.target.value, e.target.name));
    if (props.onChange) {
      props.onChange(e.target.value, e.target.name);
    }
  };

  const handleCloseEditor = (didConfirm = false) => {
    if (isEditing) {
      if (isValid) {
        toggleIsEditing();
      }
      if (props.onClose) {
        const newValue = didConfirm ? props.value : originalValue;
        props.onClose(
          newValue,
          props.name || '',
          didConfirm && Boolean(isValid)
        );
      }
    }
  };
  const handleCancel = () => handleCloseEditor(false);
  const handleConfirm = () => handleCloseEditor(true);
  const handleTextKey = e => {
    switch (e.key) {
      case 'Enter':
        handleConfirm();
        break;
      case 'Escape':
        handleCancel();
        break;
    }
  };
  const label = props.ariaLabel || props.placeholder || undefined;

  const handleIconClick =
    props.canClickText && !isEditing ? noop : toggleIsEditing;
  const handleContainerClick =
    props.canClickText && !isEditing ? toggleIsEditing : noop;

  const isPlaceholder = !props.value;
  const text = props.value || props.placeholder || '';
  // $FlowFixMe - flow isn't picking up on the default props
  const shouldBreakLines = text.length > props.breakLineLength;
  const breakIndex =
    // $FlowFixMe - flow isn't picking up on the default props
    (shouldBreakLines ? findBreakIndex(text, props.breakLineLength) : null) ||
    Infinity;

  const inner = isEditing ? (
    <input
      aria-label={label}
      autoFocus={props.autoFocus}
      css={Styles.content(props, isValid)}
      name={props.name}
      onBlur={handleConfirm}
      onChange={handleChange}
      onKeyDown={createKeyHandler(['enter', 'escape'], handleTextKey)}
      placeholder={props.placeholder}
      ref={textInput}
      size={props.size}
      type="text"
      value={props.value}
    />
  ) : props.renderText ? (
    props.renderText({ text })
  ) : (
    <div css={Styles.content(props, isValid, isPlaceholder)}>
      {shouldBreakLines ? (
        <Fragment>
          <span css={Styles.multiline} key="line1">
            {text.slice(0, breakIndex)}
          </span>
          <TruncatedText isBlock={true} key="line2">
            {text.slice(breakIndex)}
          </TruncatedText>
        </Fragment>
      ) : (
        <TruncatedText isBlock={true}>{text}</TruncatedText>
      )}
    </div>
  );

  return (
    <div
      aria-label={props.ariaLabel}
      css={Styles.root(props, isEditing, isValid)}
      onClick={handleContainerClick}
      onKeyDown={createKeyHandler(
        ['enter', 'space'],
        !isEditing ? toggleIsEditing : noop
      )}
      role="button"
      tabIndex={0}
    >
      <Icon
        color={Colors.steel}
        margin={{ right: '0.4em' }}
        name="edit"
        onClick={handleIconClick}
        size={12}
        title={props.iconTitle}
      />
      <div css={Styles.contentWrap}>{inner}</div>
    </div>
  );
};

InlineEditor.defaultProps = {
  breakLineLength: Infinity,
  canClickText: true,
  isValid: true,
  onValidate: () => true,
  placeholder: '',
  onContainerClick: () => {},
};

export default InlineEditor;
